import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@/components/Loading/Loader';
import {
  useGetVehiculeCheckById,
  useUpdateVehiculeCheck,
} from '@/hooks/vehicule-check.hooks';
import {
  TVehiculeCheckSchema,
  vehiculeCheckSchema,
} from '@/schemas/vehicule-check/vehicule-check.schema';
import { VehiculeCheckInfosManagement } from '../components/VehiculeCheckInfosManagement';
import { NotFound } from '@/components/Message/NotFound';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { useTranslation } from 'react-i18next';
import { VehiculeCheckClientManagement } from '../components/VehiculeCheckClientManagement';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { useGetTab } from '@/hooks/tab';
import { EditVehiculeCheckTab } from '@/type/ui/tab/vehiculeCheckTab';

export const EditVehiculeCheckPage = () => {
  const { t } = useTranslation('inspection');
  const { id } = useParams();
  const { data, isLoading } = useGetVehiculeCheckById(`${id}`);
  const navigate = useNavigate();

  const isEditRequestPage = location.pathname.startsWith(
    '/vehicule-check-request',
  );

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditVehiculeCheckTab.INFOS,
  });

  const {
    watch: watchVehiculeCheck,
    control: vehiculeCheckControl,
    register: registerVehiculeCheck,
    setValue: setVehiculeCheckValue,
    setError: setVehiculeCheckError,
    handleSubmit: handleSubmitVehiculeCheck,
    formState: {
      errors: vehiculeCheckErrors,
      isSubmitting: isSubmittingVehiculeCheck,
    },
  } = useForm<TVehiculeCheckSchema>({
    resolver: zodResolver(vehiculeCheckSchema),
  });

  const {
    mutate: mutateVehiculeCheck,
    isPending: isPendingUpdateVehiculeCheck,
  } = useUpdateVehiculeCheck({
    t,
    handleSuccess: () =>
      isEditRequestPage
        ? navigate('/vehicule-check-requests')
        : navigate('/vehicule-checks'),
  });

  const onSubmitVehiculeCheck = (vehiculeCheck: TVehiculeCheckSchema) => {
    const vehiculeCheckWithId = {
      id,
      ...vehiculeCheck,
    };

    mutateVehiculeCheck(vehiculeCheckWithId);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <PageHeader
        backToLink={
          location.pathname.startsWith('/vehicule-check')
            ? '/vehicule-checks'
            : -1
        }
        title={t('theInspection')}
        subTitle={
          data?.data?.referenceNumber ? (
            <>
              <span className="font-medium">
                {t('common:referenceNumber_short_3')} :
              </span>
              &nbsp;
              {data?.data?.referenceNumber}
            </>
          ) : undefined
        }
        subTitleIconClassName="ki-frame"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('inspectionList')}
              linkTo="/vehicule-checks"
            />
            <BreadcrumbItem text={t('common:inspection')} active />
          </BreadcrumbList>
        }
      />
      {!data?.data ? (
        <NotFound text={`${t('noInspectionFound')}...`} />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:informations')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditVehiculeCheckTab.INFOS}
              onChange={() => navigateToTab(EditVehiculeCheckTab.INFOS)}
            />
            {data?.data?.actionUserType === ActionUserType.INDIVIDUAL && (
              <TabItem
                text={t('common:client')}
                leftIcon={<i className="ki-outline ki-user" />}
                active={currentTab === EditVehiculeCheckTab.CUSTOMER}
                onChange={() => navigateToTab(EditVehiculeCheckTab.CUSTOMER)}
              />
            )}
          </TabList>
          {currentTab === EditVehiculeCheckTab.INFOS && (
            <VehiculeCheckInfosManagement<TVehiculeCheckSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchVehiculeCheck}
              control={vehiculeCheckControl}
              register={registerVehiculeCheck}
              setValue={setVehiculeCheckValue}
              setError={setVehiculeCheckError}
              errors={vehiculeCheckErrors}
              submitDisabled={
                isSubmittingVehiculeCheck || isPendingUpdateVehiculeCheck
              }
              onSubmit={handleSubmitVehiculeCheck(onSubmitVehiculeCheck)}
            />
          )}
          {currentTab === EditVehiculeCheckTab.CUSTOMER && (
            <VehiculeCheckClientManagement<TVehiculeCheckSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchVehiculeCheck}
              control={vehiculeCheckControl}
              register={registerVehiculeCheck}
              setValue={setVehiculeCheckValue}
              setError={setVehiculeCheckError}
              errors={vehiculeCheckErrors}
              submitDisabled={
                isSubmittingVehiculeCheck || isPendingUpdateVehiculeCheck
              }
              onSubmit={handleSubmitVehiculeCheck(onSubmitVehiculeCheck)}
            />
          )}
        </div>
      )}
    </>
  );
};
